<template>
  <div class="feed-form" v-if="item">
    <div class="feed-form__tabs-shell">
      <v-tabs v-model="activeTab" background-color="transparent" class="feed-form__tabs">
        <v-tab>
          <v-icon left small>mdi-rss</v-icon>
          Feed
        </v-tab>
        <v-tab :disabled="!item.id">
          <v-icon left small>mdi-web</v-icon>
          Domains
        </v-tab>
        <v-tab :disabled="!item.id">
          <v-icon left small>mdi-gamepad-variant</v-icon>
          Games
        </v-tab>
      </v-tabs>

      <v-tabs-items v-model="activeTab" class="feed-form__tabs-items">
        <v-tab-item>
          <div class="feed-form__tab-panel">
            <div class="feed-form__tab-panel-body feed-form__tab-panel-body--padded">
              <v-form ref="feedForm" v-model="valid">
                <v-card class="mb-4 feed-form__section" elevation="1">
                  <v-card-title class="d-flex align-center feed-form__section-title">
                    <v-icon class="mr-2">mdi-rss</v-icon>
                    <span class="font-weight-bold">Feed</span>
                  </v-card-title>
                  <v-card-text>
                    <v-row dense>
                      <v-col cols="12" md="6">
                        <v-text-field
                          v-model="item.name"
                          label="Name *"
                          :rules="[rules.required]"
                          dense
                          hide-details="auto"
                          data-testid="feed-form-name"
                        />
                      </v-col>
                      <v-col cols="12" md="6">
                        <v-select
                          v-model="item.environment"
                          label="Environment *"
                          :items="['Development', 'Production']"
                          :rules="[rules.required]"
                          dense
                          hide-details="auto"
                          data-testid="feed-form-environment"
                        />
                      </v-col>
                    </v-row>
                  </v-card-text>
                </v-card>

                <div class="feed-form__save mb-2">
                  <v-btn color="primary" large class="mr-2" data-testid="feed-form-save" @click="saveFeed()">
                    {{ item && item.id ? 'Save' : 'Create' }}
                  </v-btn>
                  <v-btn outlined large class="mr-2" :to="'/feeds'" data-testid="feed-form-cancel">
                    Cancel
                  </v-btn>
                  <v-btn
                    v-if="item.id"
                    outlined
                    large
                    color="secondary"
                    :to="changeLogLink"
                    target="_blank"
                    rel="noopener noreferrer"
                    data-testid="feed-form-change-history"
                  >
                    <v-icon left>mdi-history</v-icon>
                    History
                  </v-btn>
                </div>
              </v-form>
            </div>
          </div>
        </v-tab-item>

        <v-tab-item>
          <div class="feed-form__tab-panel">
            <div
              class="feed-form__tab-panel-body"
              v-if="item.id && visitedTabs[1]"
            >
              <v-card class="mb-0 feed-domains" elevation="1">
                <v-card-title class="d-flex align-center">
                  <v-icon class="mr-2">mdi-web</v-icon>
                  <span class="font-weight-bold">Domains</span>
                  <v-spacer />
                  <v-btn
                    v-if="domains.length > 0"
                    class="mr-2"
                    outlined
                    color="info"
                    @click="exportDomainsToExcel"
                  >
                    <v-icon left>mdi-file-excel</v-icon>
                    Export
                  </v-btn>
                  <v-btn
                    color="success"
                    :disabled="!item || !item.id"
                    data-testid="add-domain-btn"
                    @click="openAddDomainDialog"
                  >
                    Add
                  </v-btn>
                </v-card-title>
                <v-card-text class="feed-domains__search py-1">
                  <v-text-field
                    v-model="domainsSearch"
                    label="Search"
                    dense
                    hide-details
                    class="mt-0"
                    prepend-icon="mdi-magnify"
                  />
                </v-card-text>
                <v-data-table
                  class="feed-domains__table"
                  :headers="domainHeaders"
                  :items="domains"
                  :search="domainsSearch"
                  :loading="domainsLoading"
                  item-key="id"
                  sort-by="name"
                  :items-per-page="10"
                  :footer-props="{ itemsPerPageOptions: [10, 25, 50, -1] }"
                >
                  <template v-slot:item.name="{ item: domain }">
                    <a
                      :href="`https://${domain.name || domain}`"
                      target="_blank"
                      rel="noopener noreferrer"
                      class="domain-link text-truncate d-inline-block feed-domains__preview"
                      :title="domain.name || domain"
                    >
                      {{ domain.name || domain }}
                    </a>
                  </template>
                  <template v-slot:item.actions="{ item: domain }">
                    <div class="feed-domains__actions">
                      <v-tooltip bottom>
                        <template v-slot:activator="{ on, attrs }">
                          <v-icon
                            color="primary"
                            v-bind="attrs"
                            v-on="on"
                            @click="editArena(domain)"
                          >
                            mdi-pencil
                          </v-icon>
                        </template>
                        <span>Edit Arena</span>
                      </v-tooltip>
                      <v-tooltip v-if="domain.id" bottom>
                        <template v-slot:activator="{ on, attrs }">
                          <v-icon
                            color="secondary"
                            tag="a"
                            :href="domainChangeLogHref(domain)"
                            target="_blank"
                            rel="noopener noreferrer"
                            :data-testid="`feed-domain-history-${domain.id}`"
                            v-bind="attrs"
                            v-on="on"
                          >
                            mdi-history
                          </v-icon>
                        </template>
                        <span>View change history</span>
                      </v-tooltip>
                      <v-tooltip bottom>
                        <template v-slot:activator="{ on, attrs }">
                          <v-icon
                            color="warning"
                            v-bind="attrs"
                            v-on="on"
                            :data-testid="domain.id ? `feed-domain-delete-${domain.id}` : undefined"
                            @click="confirmDeleteDomain(domain)"
                          >
                            mdi-delete
                          </v-icon>
                        </template>
                        <span>Delete Domain</span>
                      </v-tooltip>
                    </div>
                  </template>
                  <template v-slot:no-data>
                    <div class="text-center pa-6">
                      <v-icon size="64" color="grey lighten-1">mdi-web-off</v-icon>
                      <p class="mt-2 mb-0 grey--text">No domains yet. Add your first domain to get started.</p>
                    </div>
                  </template>
                </v-data-table>

                <v-dialog v-model="showAddDomainDialog" max-width="480" persistent>
                  <v-card class="admin-dialog">
                    <v-card-title class="d-flex align-center admin-dialog__title">
                      <span class="font-weight-bold">Add Domain</span>
                      <v-spacer />
                      <v-btn icon small @click="cancelAddDomain">
                        <v-icon>mdi-close</v-icon>
                      </v-btn>
                    </v-card-title>
                    <v-card-text class="admin-dialog__body">
                      <v-text-field
                        v-model="newDomainName"
                        label="Domain *"
                        :disabled="addDomainLoading"
                        :error="!!newDomainError"
                        :error-messages="newDomainError"
                        dense
                        outlined
                        hide-details="auto"
                        placeholder="example.com"
                        data-testid="add-domain-input"
                        @keyup.enter="addDomain"
                      />
                    </v-card-text>
                    <v-card-actions class="admin-dialog__actions">
                      <v-spacer />
                      <v-btn
                        color="primary"
                        class="mr-2"
                        :loading="addDomainLoading"
                        :disabled="!newDomainName || addDomainLoading"
                        data-testid="add-domain-save"
                        @click="addDomain"
                      >
                        Save
                      </v-btn>
                      <v-btn outlined @click="cancelAddDomain">Cancel</v-btn>
                    </v-card-actions>
                  </v-card>
                </v-dialog>
              </v-card>
            </div>
            <v-alert v-else-if="!item.id" type="info" dense outlined class="ma-4">
              Save the feed first to manage domains.
            </v-alert>
          </div>
        </v-tab-item>

        <v-tab-item>
          <div class="feed-form__tab-panel">
            <div class="feed-form__tab-panel-body" v-if="item.id && visitedTabs[2]">
              <v-card class="mb-0 feed-form__games" elevation="1">
                <v-card-title class="d-flex align-center">
                  <v-icon class="mr-2">mdi-gamepad-variant</v-icon>
                  <span class="font-weight-bold">Games</span>
                  <v-spacer />
                  <v-menu offset-y left>
                    <template v-slot:activator="{ on, attrs }">
                      <v-btn
                        class="mr-2"
                        outlined
                        color="info"
                        :disabled="!allFeedGames.length"
                        data-testid="feed-games-export"
                        v-bind="attrs"
                        v-on="on"
                      >
                        <v-icon left>mdi-file-excel</v-icon>
                        Export
                        <v-icon right>mdi-chevron-down</v-icon>
                      </v-btn>
                    </template>
                    <v-list>
                      <v-list-item @click="exportFeedGames('filtered')">
                        <v-list-item-title>
                          Export Filtered Games ({{ visibleFeedGames.length }})
                        </v-list-item-title>
                      </v-list-item>
                      <v-list-item @click="exportFeedGames('all')">
                        <v-list-item-title>
                          Export All Games ({{ allFeedGames.length }})
                        </v-list-item-title>
                      </v-list-item>
                    </v-list>
                  </v-menu>
                  <v-btn
                    v-if="selectedFeedGames.length > 0"
                    class="mr-2"
                    color="warning"
                    outlined
                    @click="deleteSelectedFeedGames"
                  >
                    Delete selected
                  </v-btn>
                  <v-dialog v-model="arenaDialog" width="1600" style="max-width: 95vw;" persistent>
                    <template v-slot:activator="{ on, attrs }">
                      <v-btn
                        class="mr-2"
                        color="info"
                        v-bind="attrs"
                        v-on="on"
                        @click="openArenaDialog"
                      >
                        <v-icon left>mdi-file-import</v-icon>
                        Import from Arena XML
                      </v-btn>
                    </template>
                    <v-card class="admin-dialog">
                      <v-card-title class="d-flex align-center admin-dialog__title">
                        <span class="font-weight-bold">Import games from Arena XML</span>
                        <v-spacer />
                        <v-btn icon small @click="closeArenaDialog">
                          <v-icon>mdi-close</v-icon>
                        </v-btn>
                      </v-card-title>
                      <v-card-text class="admin-dialog__body">
                        <v-text-field
                          class="mb-3"
                          label="Arena XML URL"
                          v-model="arenaUrl"
                          :disabled="arenaLoading"
                          :error="!!arenaUrlError"
                          :error-messages="arenaUrlError"
                          hint="URL must contain /arenaapi"
                          persistent-hint
                          dense
                          outlined
                        />
                        <v-select
                          class="mb-3"
                          label="Meta locale"
                          v-model="arenaLocale"
                          :items="arenaLocales"
                          item-text="name"
                          item-value="value"
                          :disabled="arenaLoading || arenaImporting"
                          dense
                          outlined
                          hide-details
                        />
                        <v-alert
                          v-if="arenaError"
                          type="error"
                          outlined
                          class="mb-3"
                        >
                          {{ arenaError }}
                        </v-alert>
                        <v-text-field
                          class="mb-3"
                          label="Search by name, key or slug"
                          v-model="arenaSearch"
                          :disabled="arenaLoading"
                          dense
                          outlined
                          hide-details
                          prepend-inner-icon="mdi-magnify"
                        />
                        <v-data-table
                          class="admin-dialog__table"
                          dense
                          :headers="arenaGamesHeadersComputed"
                          :items="arenaGamesToShow"
                          :loading="arenaLoading"
                          :search="arenaSearch"
                          :items-per-page="10"
                          :footer-props="{ itemsPerPageOptions: [10, 25, 50, -1] }"
                        >
                          <template v-slot:item.game="{ item }">
                            <v-autocomplete
                              v-model="item.selectedGameId"
                              :items="games"
                              :item-text="gameSelectText"
                              item-value="id"
                              dense
                              outlined
                              hide-details
                              clearable
                              label="Select game"
                              @change="onArenaSelectedGameChange(item)"
                            />
                          </template>
                          <template v-slot:item.alias="{ item }">
                            <v-text-field
                              :value="item.alias || ''"
                              dense
                              outlined
                              hide-details
                              readonly
                              disabled
                              placeholder="Alias"
                            />
                          </template>
                          <template v-slot:item.meta="{ item }">
                            <v-text-field
                              :value="item.metaTemplateName || ''"
                              dense
                              outlined
                              hide-details
                              readonly
                              disabled
                              placeholder="Meta"
                            />
                          </template>
                        </v-data-table>
                      </v-card-text>
                      <v-card-actions class="admin-dialog__actions">
                        <v-spacer />
                        <v-btn
                          color="primary"
                          class="mr-2"
                          :loading="arenaImporting"
                          :disabled="arenaLoading || arenaImporting || !item || !item.id || !hasSelectedArenaGames"
                          @click="importArenaGames"
                        >
                          Add to Feed
                        </v-btn>
                        <v-btn
                          color="secondary"
                          class="mr-2"
                          :disabled="arenaLoading || arenaImporting || !arenaUrl"
                          :loading="arenaLoading"
                          @click="loadArenaXml"
                        >
                          Load XML
                        </v-btn>
                        <v-btn outlined @click="closeArenaDialog">Cancel</v-btn>
                      </v-card-actions>
                    </v-card>
                  </v-dialog>
                  <v-dialog v-model="dialog" width="800" persistent>
                    <template v-slot:activator="{ on, attrs }">
                      <v-btn color="success" v-bind="attrs" v-on="on">Add</v-btn>
                    </template>
                    <v-card class="admin-dialog">
                      <v-card-title class="d-flex align-center admin-dialog__title">
                        <span class="font-weight-bold">Add games to feed "{{item.name}}" (#{{item.id}})</span>
                        <v-spacer />
                        <v-btn icon small @click="closeAddGamesDialog">
                          <v-icon>mdi-close</v-icon>
                        </v-btn>
                      </v-card-title>
                      <v-card-text class="admin-dialog__body">
                        <v-text-field
                          v-model="gamesSearch"
                          label="Search"
                          dense
                          outlined
                          hide-details
                          class="mb-3"
                          prepend-inner-icon="mdi-magnify"
                        />
                        <v-data-table
                          class="admin-dialog__table"
                          dense
                          sort-by="id"
                          show-select
                          :search="gamesSearch"
                          :headers="gamesHeaders"
                          :items="absentGames"
                          v-model="selectedGames"
                          :items-per-page="10"
                          :footer-props="{ itemsPerPageOptions: [10, 25, 50, -1] }"
                        >
                          <template v-slot:item.version="{ item: game }">
                            <v-select
                              :value="addGameVersionById[game.id] ?? null"
                              :items="versionOptionsForGame(game.id)"
                              item-text="version"
                              item-value="id"
                              dense
                              hide-details
                              class="mt-0 feed-form__add-game-version"
                              :loading="!!addGameVersionsLoading[game.id]"
                              :data-testid="`add-game-version-${game.id}`"
                              @focus="ensureGameVersions(game.id)"
                              @click="ensureGameVersions(game.id)"
                              @change="setAddGameVersion(game.id, $event)"
                            />
                          </template>
                        </v-data-table>
                      </v-card-text>
                      <v-card-actions class="admin-dialog__actions">
                        <v-spacer />
                        <v-btn
                          color="primary"
                          class="mr-2"
                          :disabled="!selectedGames.length"
                          @click="addGames()"
                        >
                          Save
                        </v-btn>
                        <v-btn outlined @click="closeAddGamesDialog">Cancel</v-btn>
                      </v-card-actions>
                    </v-card>
                  </v-dialog>
                </v-card-title>

                <v-card-text class="feed-form__games-search py-1">
                  <v-text-field
                    label="Search"
                    v-model="search"
                    dense
                    hide-details
                    class="mt-0"
                    prepend-icon="mdi-magnify"
                  />
                </v-card-text>

                <v-data-table
                  class="feed-form__games-table"
                  :options.sync="feedGamesTableOptions"
                  :loading="!item"
                  :search="search"
                  :headers="feedGamesHeaders"
                  :items="item.feedGames"
                  show-select
                  item-key="id"
                  v-model="selectedFeedGames"
                  :footer-props="{ itemsPerPageOptions: [10, 25, 50, -1] }"
                >
                  <template v-slot:item.version="{ item }">
                    <v-select
                      v-model="item.gameVersionId"
                      :items="versionOptionsForFeedGame(item)"
                      item-text="version"
                      item-value="id"
                      dense
                      hide-details
                      class="mt-0 feed-form__add-game-version"
                      :loading="!!addGameVersionsLoading[item.gameId || item.game?.id]"
                      :data-testid="`feed-game-version-${item.id}`"
                      @focus="onFeedGameVersionFocus(item)"
                      @click="ensureGameVersions(item.gameId || item.game?.id)"
                      @change="onFeedGameVersionChange(item)"
                    />
                  </template>
                  <template v-slot:item.metaTemplate="{ item }">
                    <div class="d-flex align-center feed-game-meta-cell">
                      <v-autocomplete
                        :key="feedGameMetaTemplateRenderKey(item)"
                        :value="feedGameMetaTemplateId(item)"
                        :items="sortedMetaTemplatesForFeedGame(item)"
                        item-text="name"
                        item-value="id"
                        dense
                        hide-details
                        clearable
                        hide-no-data
                        class="flex-grow-1"
                        :loading="metaTemplateSavingId === item.id"
                        @input="onFeedGameMetaTemplateChange(item, $event)"
                      />
                      <a
                        v-if="feedGameMetaTemplateId(item)"
                        :href="feedGameMetaTemplateHref(feedGameMetaTemplateId(item))"
                        target="_blank"
                        rel="noopener noreferrer"
                        class="feed-game-meta-link ml-1"
                        title="Open meta template in new tab"
                        aria-label="Open meta template in new tab"
                      >
                        <v-icon small>mdi-open-in-new</v-icon>
                      </a>
                    </div>
                  </template>
                  <template v-slot:item.actions="{ item }">
                    <div class="feed-form__games-actions">
                      <v-tooltip bottom>
                        <template v-slot:activator="{ on, attrs }">
                          <v-icon
                            color="primary"
                            v-bind="attrs"
                            v-on="on"
                            @click="editItem(item)"
                          >
                            mdi-pencil
                          </v-icon>
                        </template>
                        <span>Edit</span>
                      </v-tooltip>
                      <v-tooltip v-if="item.id" bottom>
                        <template v-slot:activator="{ on, attrs }">
                          <v-icon
                            color="secondary"
                            tag="a"
                            :href="feedGameChangeLogHref(item)"
                            target="_blank"
                            rel="noopener noreferrer"
                            :data-testid="`feed-game-history-${item.id}`"
                            v-bind="attrs"
                            v-on="on"
                          >
                            mdi-history
                          </v-icon>
                        </template>
                        <span>View change history</span>
                      </v-tooltip>
                      <v-tooltip bottom>
                        <template v-slot:activator="{ on, attrs }">
                          <v-icon
                            color="warning"
                            v-bind="attrs"
                            v-on="on"
                            @click="deleteItem(item)"
                          >
                            mdi-delete
                          </v-icon>
                        </template>
                        <span>Delete</span>
                      </v-tooltip>
                    </div>
                  </template>
                </v-data-table>
              </v-card>
            </div>
            <v-alert v-else-if="!item.id" type="info" dense outlined class="ma-4">
              Save the feed first to add games.
            </v-alert>
          </div>
        </v-tab-item>
      </v-tabs-items>
    </div>
  </div>
</template>

<script>
import BasicTemplate from '../../templates/basic.vue';
import { locales as arenaLocales } from '@/constants/locales';
import { exportFeedGamesToExcel } from '../../utils/gameExcelExport';
import { apiErrorMessage } from '../../constants/analyticsConfigTypes';
import persistentDialogEsc from '@/mixins/persistentDialogEsc';

const FEED_TABS = ['feed', 'domains', 'games'];

function tabIndexFromQuery(tab, allowSecondaryTabs) {
  const key = String(tab || 'feed').toLowerCase();
  const index = FEED_TABS.indexOf(key);
  if (index <= 0) return 0;
  return allowSecondaryTabs ? index : 0;
}

function tabNameFromIndex(index) {
  return FEED_TABS[index] || 'feed';
}

export default {
  name: 'FeedForm',
  components: { BasicTemplate },
  mixins: [
    persistentDialogEsc({
      showAddDomainDialog: { close: 'cancelAddDomain', busy: 'addDomainLoading' },
      arenaDialog: { close: 'closeArenaDialog', busy: 'arenaImporting' },
      dialog: 'closeAddGamesDialog',
    }),
  ],
  props: {
    item: {},
    games: [],
    metaTemplates: [],
  },
  data() {
    return {
      activeTab: 0,
      visitedTabs: { 0: true },
      dialog: false,
      search: '',
      gamesSearch: '',
      valid: true,
      rules: {
        required: (v) => !!(v != null && String(v).trim()) || 'Required.',
      },
      selectedGames: [],
      addGameVersionById: {},
      addGameVersionsCache: {},
      addGameVersionsLoading: {},
      selectedFeedGames: [],
      feedGamesTableOptions: {
        sortBy: ['game.slug'],
        sortDesc: [false],
        itemsPerPage: 10,
      },
      domains: [],
      domainsSearch: '',
      domainsLoading: false,
      domainHeaders: [
        { text: 'Domain', value: 'name', sortable: true },
        {
          text: 'Actions',
          value: 'actions',
          sortable: false,
          align: 'center',
          width: '1%',
          class: 'feed-domains__col--compact',
          cellClass: 'feed-domains__col--compact',
        },
      ],
      showAddDomainDialog: false,
      newDomainName: '',
      newDomainError: '',
      addDomainLoading: false,
      arenaDialog: false,
      arenaGames: [],
      arenaGamesHeaders: [
        { text: 'Name', value: 'name' },
        { text: 'Legacy Key', value: 'key' },
        { text: 'URL', value: 'url' },
        { text: 'Slug', value: 'slug' },
        { text: 'Game', value: 'game' },
      ],
      arenaUrl: '',
      arenaUrlError: '',
      arenaLocale: 'en',
      arenaLocales,
      arenaSearch: '',
      arenaLoading: false,
      arenaImporting: false,
      arenaImportedGameIds: [],
      arenaImportedAliases: [],
      arenaResolvedAliasesFromMetas: [],
      arenaCreatedMetaTemplatesByName: {},
      arenaMetaTemplateSupportedGameIdsById: {},
      arenaMetaTemplateHasLocaleById: {},
      arenaAllMetaTemplates: null,
      arenaError: '',
      metaTemplateSavingId: null,
      feedGameMetaTemplateRenderKeys: {},
      gamesHeaders: [
        { text: 'Id', value: 'id', width: '72px' },
        { text: 'Slug', value: 'slug' },
        { text: 'Name', value: 'name' },
        { text: 'Version', value: 'version', sortable: false, width: '180px' },
      ],
      feedGamesHeaders: [
        { text: 'Id', value: 'game.id' },
        { text: 'Slug', value: 'game.slug' },
        { text: 'Name', value: 'game.name' },
        { text: 'Version', value: 'version', sortable: false, width: '180px' },
        { text: 'Meta', value: 'metaTemplate' },
        {
          text: 'Actions',
          value: 'actions',
          align: 'center',
          sortable: false,
          width: '1%',
          class: 'feed-form__games-col--compact',
          cellClass: 'feed-form__games-col--compact',
        },
      ]
    };
  },
  computed: {
    changeLogLink() {
      return {
        path: '/game-data-change-log',
        query: {
          table: 'feed',
          entityId: String(this.item.id),
        },
      };
    },
    absentGames() {
      return this.games.filter(g => !this.item.feedGames.some(fg => fg.gameId == g.id));
    },
    allFeedGames() {
      return Array.isArray(this.item?.feedGames) ? this.item.feedGames : [];
    },
    visibleFeedGames() {
      const query = (this.search || '').trim().toLowerCase();
      if (!query) return this.allFeedGames;
      return this.allFeedGames.filter((item) => this.feedGameMatchesSearch(item, query));
    },
    baseFeedGameIdSet() {
      const feedGames = Array.isArray(this.item?.feedGames) ? this.item.feedGames : [];
      const ids = new Set();
      for (const fg of feedGames) {
        if (fg && fg.gameId != null) {
          ids.add(Number(fg.gameId));
        }
      }
      return ids;
    },
    effectiveFeedGameAliasSet() {
      const set = new Set();
      const resolved = Array.isArray(this.arenaResolvedAliasesFromMetas) ? this.arenaResolvedAliasesFromMetas : [];
      for (const a of resolved) {
        if (a) set.add(String(a));
      }
      const imported = Array.isArray(this.arenaImportedAliases) ? this.arenaImportedAliases : [];
      for (const a of imported) {
        if (a) set.add(String(a));
      }
      return set;
    },
    effectiveFeedGameIdSet() {
      const ids = new Set(this.baseFeedGameIdSet);
      const imported = Array.isArray(this.arenaImportedGameIds) ? this.arenaImportedGameIds : [];
      for (const id of imported) {
        if (id != null) ids.add(Number(id));
      }
      return ids;
    },
    arenaGamesToShow() {
      // Hide rows where the Arena XML game is already mapped to a game
      // that is already present in this feed.
      if (!Array.isArray(this.arenaGames)) return [];
      const feedIds = this.effectiveFeedGameIdSet;
      const aliasSet = this.effectiveFeedGameAliasSet;
      return this.arenaGames.filter(g => {
        // Hide if Arena XML slug already exists as feedGame.alias (even if not mapped yet).
        if (g?.slug && aliasSet.has(String(g.slug))) return false;

        const mappedId = g?.selectedGameId;
        if (mappedId == null) return true;
        // Hide if selected game is already in feed.
        if (feedIds.has(Number(mappedId))) return false;
        return true;
      });
    },
    arenaGamesHeadersComputed() {
      const base = Array.isArray(this.arenaGamesHeaders) ? this.arenaGamesHeaders : [];
      const anyNeedsAlias = Array.isArray(this.arenaGamesToShow) && this.arenaGamesToShow.some(g => this.arenaNeedsAlias(g));
      if (!anyNeedsAlias) {
        return base;
      }
      // Add Alias column at the end.
      const out = [...base];
      if (!out.some(h => h && h.value === 'alias')) {
        out.push({ text: 'Alias', value: 'alias' });
      }
      if (!out.some(h => h && h.value === 'meta')) {
        out.push({ text: 'Meta', value: 'meta' });
      }
      return out;
    },
    hasSelectedArenaGames() {
      if (!Array.isArray(this.arenaGamesToShow)) return false;
      return this.arenaGamesToShow.some(g => g && g.selectedGameId);
    },
  },
  async created() {
    this.syncTabFromRoute();
    this.markTabVisited(this.activeTab);
    // Ensure authentication before component loads
    try {
      await this.$msal.ensureAuthenticated();
    } catch (error) {
      console.error('Authentication failed:', error);
    }
  },
  watch: {
    dialog(open) {
      if (open) {
        this.gamesSearch = '';
        this.selectedGames = [];
        this.addGameVersionById = {};
        this.addGameVersionsCache = {};
        this.addGameVersionsLoading = {};
      }
    },
    '$route.query.tab'() {
      this.syncTabFromRoute();
    },
    activeTab(index) {
      this.markTabVisited(index);
      this.syncTabToRoute(index);
    },
    item: {
      handler(newItem, oldItem) {
        if (newItem && newItem.id) {
          this.loadDomains();
          this.syncTabFromRoute();
          this.markTabVisited(this.activeTab);
        } else if (!this.$route.params.id && this.activeTab !== 0) {
          this.activeTab = 0;
        }
      },
      immediate: true
    },
    arenaLocale() {
      if (this.arenaDialog) {
        this.preloadFeedAliasesFromMetaTemplates();
      }
    }
  },
  methods: {
      markTabVisited(index) {
        if (!this.visitedTabs[index]) {
          this.$set(this.visitedTabs, index, true);
        }
      },
      domainChangeLogLink(domain) {
        return {
          path: '/game-data-change-log',
          query: {
            table: 'domain',
            entityId: String(domain.id),
          },
        };
      },
      domainChangeLogHref(domain) {
        return this.$router.resolve(this.domainChangeLogLink(domain)).href;
      },
      feedGameChangeLogLink(feedGame) {
        return {
          path: '/game-data-change-log',
          query: {
            table: 'feed_game',
            entityId: String(feedGame.id),
          },
        };
      },
      feedGameChangeLogHref(feedGame) {
        return this.$router.resolve(this.feedGameChangeLogLink(feedGame)).href;
      },
      syncTabFromRoute() {
        const allowSecondaryTabs = !!(this.item?.id || this.$route.params.id);
        const next = tabIndexFromQuery(this.$route.query.tab, allowSecondaryTabs);
        if (this.activeTab !== next) {
          this.activeTab = next;
        } else {
          this.markTabVisited(next);
        }
      },
      syncTabToRoute(index) {
        const allowSecondaryTabs = !!(this.item?.id || this.$route.params.id);
        if (!allowSecondaryTabs && index !== 0) {
          this.activeTab = 0;
          return;
        }
        const tab = tabNameFromIndex(index);
        const current = String(this.$route.query.tab || 'feed').toLowerCase();
        if (current === tab) return;
        this.$router.replace({
          query: {
            ...this.$route.query,
            tab,
          },
        });
      },
      getArenaLocale() {
        return this.arenaLocale || 'en';
      },
      getMetaForLocale(metas, locale) {
        if (!Array.isArray(metas) || !locale) return null;
        return metas.find(m => m && m.locale === locale) || null;
      },
      getMetaAliasForLocale(metas, locale, fallbackLocale = 'en') {
        const primary = this.getMetaForLocale(metas, locale);
        const primaryAlias = primary?.alias == null ? '' : String(primary.alias).trim();
        if (primaryAlias) return primaryAlias;
        if (fallbackLocale && locale !== fallbackLocale) {
          const fallback = this.getMetaForLocale(metas, fallbackLocale);
          const fallbackAlias = fallback?.alias == null ? '' : String(fallback.alias).trim();
          if (fallbackAlias) return fallbackAlias;
        }
        return '';
      },
      sleep(ms) {
        return new Promise(resolve => setTimeout(resolve, ms));
      },
      computeSyndicationBaseMetaTemplateName(gameSlug) {
        const slug = String(gameSlug || '').trim();
        if (!slug) return '';
        return `syndication - ${slug}`;
      },
      async preloadFeedAliasesFromMetaTemplates() {
        // Some backends store "syndication alias" inside MetaTemplate locale meta, not in feedGame.alias.
        // We resolve those aliases here so the import dialog can correctly hide already-added rows.
        try {
          const feedGames = Array.isArray(this.item?.feedGames) ? this.item.feedGames : [];
          const templateIds = new Set(
            feedGames
              .map(fg => fg?.metaTemplateId)
              .filter(Boolean)
              .map(id => String(id))
          );
          const locale = this.getArenaLocale();

          const resolved = new Set();
          for (const idStr of templateIds) {
            try {
              const metas = await this.$gameApi.get(`/metaTemplates/${idStr}/metas`);
              const alias = this.getMetaAliasForLocale(metas, locale, 'en');
              if (alias) {
                resolved.add(alias);
              }
            } catch (e) {
              // ignore per-template failures
            }
            await this.sleep(100);
          }

          this.arenaResolvedAliasesFromMetas = Array.from(resolved);
        } catch (e) {
          this.arenaResolvedAliasesFromMetas = [];
        }
      },
      getExistingMetaTemplateNamesSet() {
        const set = new Set();
        const templates = Array.isArray(this.metaTemplates) ? this.metaTemplates : [];
        for (const t of templates) {
          if (t && t.name) set.add(String(t.name));
        }
        const created = this.arenaCreatedMetaTemplatesByName || {};
        for (const name of Object.keys(created)) {
          set.add(String(name));
        }
        return set;
      },
      async loadAllMetaTemplatesOnce() {
        if (Array.isArray(this.arenaAllMetaTemplates)) return this.arenaAllMetaTemplates;
        try {
          const resp = await this.$gameApi.get('/meta-templates');
          if (Array.isArray(resp)) {
            this.arenaAllMetaTemplates = resp;
          } else if (Array.isArray(resp?.items)) {
            this.arenaAllMetaTemplates = resp.items;
          } else if (Array.isArray(resp?.data)) {
            this.arenaAllMetaTemplates = resp.data;
          } else {
            this.arenaAllMetaTemplates = [];
          }
        } catch (e) {
          this.arenaAllMetaTemplates = [];
        }
        return this.arenaAllMetaTemplates;
      },
      async findMetaTemplateByExactName(name) {
        const target = String(name || '').trim();
        if (!target) return null;
        const local = Array.isArray(this.metaTemplates) ? this.metaTemplates : [];
        const localHit = local.find(t => t && t.name === target);
        if (localHit) return localHit;
        const all = await this.loadAllMetaTemplatesOnce();
        return Array.isArray(all) ? all.find(t => t && t.name === target) : null;
      },
      async resolveSyndicationMetaTemplate(gameSlug, expectedAlias) {
        const baseName = this.computeSyndicationBaseMetaTemplateName(gameSlug);
        if (!baseName) return null;

        const existingBase = await this.findMetaTemplateByExactName(baseName);
        if (!existingBase?.id) return null;

        const templateId = existingBase.id;
        const expected = String(expectedAlias || '').trim();
        if (!expected) return null;

        const locale = this.getArenaLocale();
        let metas;
        try {
          metas = await this.$gameApi.get(`/metaTemplates/${templateId}/metas`);
        } catch (e) {
          return null;
        }

        const selectedMeta = this.getMetaForLocale(metas, locale);
        if (selectedMeta) {
          const selectedAlias = selectedMeta.alias == null ? '' : String(selectedMeta.alias).trim();
          if (selectedAlias === expected) {
            return { metaTemplateId: templateId, localeToEnsure: null };
          }
          return null;
        }

        const enMeta = this.getMetaForLocale(metas, 'en');
        const enAlias = enMeta?.alias == null ? '' : String(enMeta.alias).trim();
        if (enAlias === expected) {
          return {
            metaTemplateId: templateId,
            localeToEnsure: locale === 'en' ? null : locale,
          };
        }

        return null;
      },
      buildLocaleMetaPayload(locale, alias) {
        // Keep payload aligned with Metadata Management "new locale" defaults.
        return {
          locale: locale || 'en',
          alias: String(alias || '').trim(),
          name: '',
          details: '',
          fullTitle: '',
          seoTitle: '',
          subTitle: '',
          htmlTitle: '',
          htmlDescription: '',
          description: '',
          rules: '',
          categories: [],
          primaryCategory: null,
          tags: [],
          badges: []
        };
      },
      async ensureMetaTemplateSupportsGame(templateId, gameId) {
        if (!templateId || !gameId) return;

        // Cache: templateId -> Set(gameIds)
        let idSet = this.arenaMetaTemplateSupportedGameIdsById[String(templateId)];
        if (!idSet) {
          try {
            const template = await this.$gameApi.get(`/meta-templates/${templateId}`);
            const supported = Array.isArray(template?.supportedGames) ? template.supportedGames : [];
            idSet = new Set(supported.map(g => String(g?.id)).filter(Boolean));
          } catch (e) {
            idSet = new Set();
          }
          this.arenaMetaTemplateSupportedGameIdsById[String(templateId)] = idSet;
        }

        const gid = String(gameId);
        if (idSet.has(gid)) return;

        // Patch supportedGames by appending the game id (avoid overwriting existing ones).
        const next = [...idSet, gid].map(id => ({ id: Number(id) }));
        await this.$gameApi.patch(`/meta-templates/${templateId}`, { supportedGames: next });
        idSet.add(gid);

        // Keep local metaTemplates list in sync for client-side filtering on the Games tab.
        const local = (this.metaTemplates || []).find(t => String(t?.id) === String(templateId));
        if (local) {
          this.$set(local, 'supportedGames', next);
        }
      },
      async ensureMetaTemplateHasLocale(templateId, alias, locale) {
        if (!templateId) return;
        const resolvedLocale = locale || this.getArenaLocale();
        const key = `${templateId}:${resolvedLocale}`;
        if (this.arenaMetaTemplateHasLocaleById[key] === true) return;

        try {
          const metas = await this.$gameApi.get(`/metaTemplates/${templateId}/metas`);
          const hasLocale = Array.isArray(metas) && metas.some(m => m && m.locale === resolvedLocale);
          if (hasLocale) {
            this.arenaMetaTemplateHasLocaleById[key] = true;
            return;
          }
        } catch (e) {
          // If we can't verify, still try to create below.
        }

        await this.$gameApi.post(
          `/metaTemplates/${templateId}/metas`,
          this.buildLocaleMetaPayload(resolvedLocale, alias)
        );
        this.arenaMetaTemplateHasLocaleById[key] = true;
      },
      computeSyndicationMetaTemplateName(gameSlug) {
        const slug = String(gameSlug || '').trim();
        if (!slug) return '';
        const existing = this.getExistingMetaTemplateNamesSet();
        let i = 1;
        while (i < 1000) {
          const name = i === 1 ? `syndication - ${slug}` : `syndication(${i}) - ${slug}`;
          if (!existing.has(name)) {
            return name;
          }
          i += 1;
        }
        return `syndication(${Date.now()}) - ${slug}`;
      },
      arenaNeedsAlias(arenaRow) {
        if (!arenaRow || !arenaRow.selectedGameId) return false;
        const selected = Array.isArray(this.games) ? this.games.find(g => g.id === arenaRow.selectedGameId) : null;
        if (!selected || !selected.slug) return false;
        if (!arenaRow.slug) return false;
        return String(selected.slug) !== String(arenaRow.slug);
      },
      onArenaSelectedGameChange(arenaRow) {
        if (!arenaRow) return;
        if (!arenaRow.selectedGameId) {
          arenaRow.alias = '';
          arenaRow.metaTemplateName = '';
          arenaRow.metaTemplateId = null;
          return;
        }
        if (this.arenaNeedsAlias(arenaRow)) {
          // Default alias to Arena XML slug when mismatched, but don't override manual edits.
          if (!arenaRow.alias) {
            arenaRow.alias = arenaRow.slug || '';
          }
          const selected = Array.isArray(this.games) ? this.games.find(g => g.id === arenaRow.selectedGameId) : null;
          const slugForName = selected?.slug || '';
          if (!arenaRow.metaTemplateName && slugForName) {
            // Prefer the base name; import will reuse existing template when present.
            arenaRow.metaTemplateName = this.computeSyndicationBaseMetaTemplateName(slugForName);
          }
        } else {
          arenaRow.alias = '';
          arenaRow.metaTemplateName = '';
          arenaRow.metaTemplateId = null;
        }
      },
      gameSelectText(game) {
        if (!game) return '';
        const parts = [];
        if (game.id != null) parts.push(`#${game.id}`);
        if (game.slug) parts.push(game.slug);
        if (game.name) parts.push(`(${game.name})`);
        return parts.join(' ');
      },
    feedGameGameId(feedGame) {
      return feedGame?.gameId ?? feedGame?.game?.id ?? null;
    },
    sortMetaTemplatesList(list) {
      return (Array.isArray(list) ? list : [])
        .slice()
        .sort((a, b) => (a?.name || '').localeCompare(b?.name || '', undefined, { sensitivity: 'base' }));
    },
    templateSupportsGameId(template, gameId) {
      if (gameId == null || !template) return false;
      const gid = String(gameId);
      if (Array.isArray(template.supportedGames)) {
        return template.supportedGames.some((g) => String(g?.id ?? g) === gid);
      }
      if (Array.isArray(template.supportedGameIds)) {
        return template.supportedGameIds.some((id) => String(id) === gid);
      }
      return false;
    },
    metaTemplatesSupportingGame(gameId) {
      if (gameId == null) return [];
      const all = Array.isArray(this.metaTemplates) ? this.metaTemplates : [];
      const hasSupportInfo = all.some(
        (t) => Array.isArray(t?.supportedGames) || Array.isArray(t?.supportedGameIds)
      );
      // If list payload has no support info, keep full list rather than empty dropdowns.
      const filtered = hasSupportInfo
        ? all.filter((t) => this.templateSupportsGameId(t, gameId))
        : all;
      return this.sortMetaTemplatesList(filtered);
    },
    sortedMetaTemplatesForFeedGame(feedGame) {
      const gameId = this.feedGameGameId(feedGame);
      let list = this.metaTemplatesSupportingGame(gameId);

      const currentId = this.feedGameMetaTemplateId(feedGame);
      if (currentId != null && !list.some((t) => Number(t?.id) === Number(currentId))) {
        const fromAll = (this.metaTemplates || []).find((t) => Number(t?.id) === Number(currentId));
        const extra = fromAll || { id: currentId, name: `#${currentId}` };
        return this.sortMetaTemplatesList([extra, ...list]);
      }
      return list;
    },
    feedGameMetaTemplateId(feedGame) {
      if (!feedGame) return null;
      return feedGame.metaTemplateId ?? feedGame?.game?.metaTemplateId ?? feedGame?.game?.metaTemplate?.id ?? null;
    },
    feedGameMetaTemplateHref(metaTemplateId) {
      if (metaTemplateId == null) return '';
      return this.$router.resolve(`/metadata-management/${metaTemplateId}`).href;
    },
    feedGameMetaTemplateRenderKey(feedGame) {
      const id = feedGame?.id;
      if (id == null) return 0;
      return this.feedGameMetaTemplateRenderKeys[String(id)] || 0;
    },
    bumpFeedGameMetaTemplateRenderKey(feedGame) {
      const id = feedGame?.id;
      if (id == null) return;
      const key = String(id);
      const next = (this.feedGameMetaTemplateRenderKeys[key] || 0) + 1;
      this.$set(this.feedGameMetaTemplateRenderKeys, key, next);
    },
    resolveMetaTemplateName(metaTemplateId, feedGame) {
      if (metaTemplateId == null) return '';
      const id = Number(metaTemplateId);
      const fromList = this.sortedMetaTemplatesForFeedGame(feedGame).find(t => Number(t?.id) === id);
      if (fromList?.name) return String(fromList.name);
      const fromAll = (this.metaTemplates || []).find(t => Number(t?.id) === id);
      if (fromAll?.name) return String(fromAll.name);
      return `#${id}`;
    },
    async confirmFeedGameMetaTemplateChange(feedGame, previousId, nextId) {
      if (previousId == null || previousId === nextId) {
        return true;
      }

      const previousName = this.resolveMetaTemplateName(previousId, feedGame);
      let title;
      let text;
      let confirmButtonText;

      if (nextId == null) {
        title = 'Remove meta template?';
        text = `Remove meta template "${previousName}" from this feed game?`;
        confirmButtonText = 'Yes, remove';
      } else {
        const nextName = this.resolveMetaTemplateName(nextId, feedGame);
        title = 'Change meta template?';
        text = `Change meta template from "${previousName}" to "${nextName}" for this feed game?`;
        confirmButtonText = 'Yes, change';
      }

      try {
        const result = await this.$swal.fire({
          icon: 'warning',
          title,
          text,
          showCancelButton: true,
          confirmButtonText,
          cancelButtonText: 'Cancel',
        });
        return !!result.isConfirmed;
      } catch (e) {
        return false;
      }
    },
    onFeedGameVersionFocus(feedGame) {
      this.$set(feedGame, '_previousGameVersionId', feedGame.gameVersionId ?? null);
      this.ensureGameVersions(feedGame.gameId || feedGame.game?.id);
    },
    async onFeedGameVersionChange(feedGame) {
      const gameId = feedGame?.gameId ?? feedGame?.game?.id;
      if (!this.item?.id || gameId == null) return;
      const previousId = feedGame._previousGameVersionId;
      const nextId = feedGame.gameVersionId ?? null;
      if (previousId === nextId) return;
      try {
        await this.$gameApi.patch(`/feeds/${this.item.id}/games/${gameId}`, {
          gameVersionId: nextId,
        });
        this.$set(feedGame, '_previousGameVersionId', nextId);
        const selected = this.versionOptionsForFeedGame(feedGame).find(v => v.id == nextId);
        if (nextId == null) {
          this.$set(feedGame, 'gameVersion', null);
        } else if (selected) {
          this.$set(feedGame, 'gameVersion', { id: selected.id, version: selected.version });
        }
        this.$emit('change', this.item);
      } catch (e) {
        if (previousId !== undefined) {
          this.$set(feedGame, 'gameVersionId', previousId);
        }
        if (this.$swal) {
          this.$swal.fire({
            icon: 'error',
            title: 'Failed to update version',
            text: e && e.message ? e.message : 'Unknown error',
            timer: 3000,
            showConfirmButton: false,
          });
        }
      }
    },
    async onFeedGameMetaTemplateChange(feedGame, metaTemplateId) {
      const gameId = feedGame?.gameId ?? feedGame?.game?.id;
      if (!this.item?.id || gameId == null) return;

      const previousId = this.feedGameMetaTemplateId(feedGame);
      const nextId = metaTemplateId || null;
      if (previousId === nextId) return;

      const confirmed = await this.confirmFeedGameMetaTemplateChange(feedGame, previousId, nextId);
      if (!confirmed) {
        this.bumpFeedGameMetaTemplateRenderKey(feedGame);
        return;
      }

      const hadOwnMetaTemplateId = Object.prototype.hasOwnProperty.call(feedGame, 'metaTemplateId');
      const ownMetaTemplateId = hadOwnMetaTemplateId ? feedGame.metaTemplateId : undefined;

      this.$set(feedGame, 'metaTemplateId', nextId);
      this.metaTemplateSavingId = feedGame.id;

      try {
        await this.$gameApi.patch(`/feeds/${this.item.id}/games/${gameId}`, {
          metaTemplateId: nextId,
        });
      } catch (e) {
        if (hadOwnMetaTemplateId) {
          this.$set(feedGame, 'metaTemplateId', ownMetaTemplateId);
        } else {
          this.$delete(feedGame, 'metaTemplateId');
        }
        await this.$swal.fire({
          icon: 'error',
          title: e.statusCode || 'Error',
          text: e.message || e.details,
          timer: 2000,
          showConfirmButton: false,
        });
      } finally {
        this.metaTemplateSavingId = null;
      }
    },
    async loadDomains() {
      if (this.item && this.item.id) {
        this.domainsLoading = true;
        try {
          // Ensure authentication before making API call
          await this.$msal.ensureAuthenticated();
          this.domains = await this.$gameApi.get(`/domains?feedId=${this.item.id}`);
        } catch (error) {
          console.error('Error loading domains:', error);
          this.domains = [];
        } finally {
          this.domainsLoading = false;
        }
      }
    },
    async activateFeedSave() {
      this.item.touched = true;
    },
    async saveFeed() {
      if (this.$refs.feedForm && !this.$refs.feedForm.validate()) {
        return;
      }
      const name = (this.item.name || '').trim();
      const environment = this.item.environment;
      if (!name || !environment) {
        return;
      }
      const body = { environment, name };
      try {
        if (this.item.id) {
          await this.$gameApi.patch(`/feeds/${this.item.id}`, body);
          this.$swal.fire({ icon: 'success', title: 'Feed saved.', timer: 1500 });
        } else {
          const newFeed = await this.$gameApi.post(`/feeds/`, body);
          this.$swal.fire({ icon: 'success', title: 'Feed created.', timer: 1500 });
          this.$router.push(`/feeds/${newFeed.id}`);
        }
      } catch (error) {
        console.error('Error saving feed:', error);
        this.$swal.fire({
          icon: 'error',
          title: 'Save failed',
          text: apiErrorMessage(error, 'Failed to save feed.'),
        });
      }
    },
    async editItem(item) {
      this.$router.push(`/feeds/${this.item.id}/games/${item.id}`);
    },
    getNestedValue(obj, path) {
      if (!path) return undefined;
      return String(path).split('.').reduce((acc, key) => (acc == null ? undefined : acc[key]), obj);
    },
    feedGameMetaTemplateLabel(feedGame) {
      const id = this.feedGameMetaTemplateId(feedGame);
      if (id == null) return '';
      const fromAll = (this.metaTemplates || []).find((t) => Number(t?.id) === Number(id));
      return fromAll?.name || `#${id}`;
    },
    feedGameMatchesSearch(item, query) {
      if (!query) return true;
      for (const header of this.feedGamesHeaders) {
        if (!header?.value || header.value === 'actions') continue;
        let value;
        if (header.value === 'metaTemplate') {
          value = this.feedGameMetaTemplateLabel(item);
        } else {
          value = this.getNestedValue(item, header.value);
        }
        if (value == null || typeof value === 'boolean') continue;
        if (String(value).toLowerCase().includes(query)) return true;
      }
      // Also match alias if present
      if (item?.alias != null && String(item.alias).toLowerCase().includes(query)) return true;
      return false;
    },
    compareFeedGameExportValues(a, b) {
      if (a == null && b == null) return 0;
      if (a == null) return -1;
      if (b == null) return 1;
      if (typeof a === 'number' && typeof b === 'number') return a - b;
      if (typeof a === 'boolean' && typeof b === 'boolean') return Number(a) - Number(b);
      return String(a).localeCompare(String(b), undefined, { numeric: true, sensitivity: 'base' });
    },
    sortFeedGamesLikeGrid(items) {
      const sortBy = this.feedGamesTableOptions?.sortBy?.[0] || 'game.slug';
      const sortDesc = !!(this.feedGamesTableOptions?.sortDesc?.[0]);
      return [...items].sort((left, right) => {
        let leftValue;
        let rightValue;
        if (sortBy === 'metaTemplate') {
          leftValue = this.feedGameMetaTemplateId(left);
          rightValue = this.feedGameMetaTemplateId(right);
        } else {
          leftValue = this.getNestedValue(left, sortBy);
          rightValue = this.getNestedValue(right, sortBy);
        }
        const cmp = this.compareFeedGameExportValues(leftValue, rightValue);
        return sortDesc ? -cmp : cmp;
      });
    },
    exportFeedGames(type = 'filtered') {
      try {
        const source = type === 'filtered' ? this.visibleFeedGames : this.allFeedGames;
        if (!source.length) {
          this.$swal.fire({
            icon: 'info',
            title: 'Nothing to export',
            text: type === 'filtered'
              ? 'No games match the current search'
              : 'This feed has no games yet',
            timer: 2000,
            showConfirmButton: false,
          });
          return;
        }

        const dataToExport = this.sortFeedGamesLikeGrid(source);
        const feedId = this.item?.id || 'feed';
        const feedSlug = String(this.item?.name || 'feed').replace(/[^\w.-]+/g, '_');
        const timestamp = new Date().toISOString().slice(0, 19).replace(/:/g, '-');
        const filename = `feed_${feedId}_${feedSlug}_games_${type}_${timestamp}.xlsx`;

        const { count } = exportFeedGamesToExcel(dataToExport, {
          feed: this.item,
          metaTemplates: this.metaTemplates,
          filename,
          sheetName: 'Feed Games',
        });

        this.$swal.fire({
          icon: 'success',
          title: 'Export Successful',
          text: `Exported ${count} feed games to ${filename}`,
          timer: 2000,
          showConfirmButton: false,
        });
      } catch (error) {
        console.error('Feed games export error:', error);
        this.$swal.fire({
          icon: 'error',
          title: 'Export Failed',
          text: error.message || 'An error occurred during export',
          timer: 3000,
          showConfirmButton: false,
        });
      }
    },
    async deleteConfirm(item) {
      this.deleteDialog = { ...item, toDelete: true };
    },
    async deleteItem(item) {
      const { isConfirmed } = await this.$swal.fire({ icon: 'warning', title: 'This will delete all feed infromation. Are you sure to proceed?', showCancelButton: true });
      if (isConfirmed) {
        await this.$gameApi.delete(`/feeds/${this.item.id}/games/${item.gameId}`);
        this.deleteDialog = {};
        this.$emit('change', this.item);
        // Reload domains after deleting games
        await this.loadDomains();
      }
    },
    async deleteSelectedFeedGames() {
      if (!this.selectedFeedGames.length || !this.item || !this.item.id) {
        return;
      }
      try {
        const result = await this.$swal.fire({
          icon: 'warning',
          title: 'Delete selected games?',
          text: `This will delete ${this.selectedFeedGames.length} game(s) from this feed. Are you sure?`,
          showCancelButton: true,
          confirmButtonText: 'Yes, delete',
          cancelButtonText: 'Cancel'
        });
        if (!result.isConfirmed) {
          return;
        }
      } catch (e) {
        return;
      }

      try {
        if (this.$msal && typeof this.$msal.ensureAuthenticated === 'function') {
          await this.$msal.ensureAuthenticated();
        }
      } catch (e) {
        console.error('Authentication failed before deleting selected feed games:', e);
      }

      try {
        const toDelete = [...this.selectedFeedGames];
        await Promise.all(
          toDelete.map(fg =>
            this.$gameApi.delete(`/feeds/${this.item.id}/games/${fg.gameId}`)
          )
        );
        this.selectedFeedGames = [];
        this.$emit('change', this.item);
        await this.loadDomains();
        if (this.$swal) {
          this.$swal.fire({
            icon: 'success',
            title: 'Selected games deleted from feed.',
            timer: 1500,
            showConfirmButton: false
          });
        }
      } catch (e) {
        console.error('Error deleting selected feed games:', e);
        if (this.$swal) {
          this.$swal.fire({
            icon: 'error',
            title: 'Failed to delete selected games',
            text: e && e.message ? e.message : 'Unknown error',
            timer: 3000,
            showConfirmButton: false
          });
        }
      }
    },
    async addGames() {
      await Promise.all(
        this.selectedGames.map(g => this.$gameApi.post(`/feeds/${this.item.id}/games/`, {
          gameId: g.id,
          feedId: this.item.id,
          gameVersionId: this.addGameVersionById[g.id] ?? null,
        }))
      );
      this.$emit('change', this.item);
      this.selectedGames = [];
      this.addGameVersionById = {};
      this.dialog = false;
      // Reload domains after adding games
      await this.loadDomains();
    },
    versionOptionsForGame(gameId) {
      const versions = this.addGameVersionsCache[gameId] || [];
      return [{ id: null, version: 'Latest' }].concat(versions);
    },
    versionOptionsForFeedGame(feedGame) {
      const gameId = feedGame?.gameId || feedGame?.game?.id;
      const options = this.versionOptionsForGame(gameId);
      const pinnedId = feedGame?.gameVersionId;
      if (pinnedId == null) return options;
      if (options.some(v => v.id == pinnedId)) return options;
      const pinned = feedGame.gameVersion;
      const label = pinned?.version || pinned?.name || feedGame.version;
      if (!label) return options;
      return options.concat([{ id: pinnedId, version: label }]);
    },
    setAddGameVersion(gameId, versionId) {
      this.$set(this.addGameVersionById, gameId, versionId);
    },
    async ensureGameVersions(gameId) {
      if (!gameId || this.addGameVersionsCache[gameId] || this.addGameVersionsLoading[gameId]) {
        return;
      }
      this.$set(this.addGameVersionsLoading, gameId, true);
      try {
        const versions = await this.$gameApi.get(`/games/${gameId}/versions`);
        this.$set(this.addGameVersionsCache, gameId, Array.isArray(versions) ? versions : []);
      } catch (e) {
        console.error('Error loading game versions:', e);
        this.$set(this.addGameVersionsCache, gameId, []);
      } finally {
        this.$set(this.addGameVersionsLoading, gameId, false);
      }
    },
      async confirmDeleteDomain(domain) {
        if (!domain || !domain.id) {
          return;
        }
        try {
          const result = await this.$swal.fire({
            icon: 'warning',
            title: 'Delete domain?',
            text: `This will unlink domain "${domain.name || domain}" from this feed. Continue?`,
            showCancelButton: true,
            confirmButtonText: 'Yes, delete',
            cancelButtonText: 'Cancel'
          });
          if (!result.isConfirmed) {
            return;
          }
        } catch (e) {
          // If swal fails, do not delete silently
          return;
        }
        try {
          if (this.$msal && typeof this.$msal.ensureAuthenticated === 'function') {
            await this.$msal.ensureAuthenticated();
          }
        } catch (e) {
          console.error('Authentication failed before deleting domain:', e);
        }
        try {
          await this.$gameApi.delete(`/domains/${domain.id}`);
          await this.loadDomains();
          if (this.$swal) {
            this.$swal.fire({
              icon: 'success',
              title: 'Domain deleted.',
              timer: 1500,
              showConfirmButton: false
            });
          }
        } catch (e) {
          console.error('Error deleting domain:', e);
          if (this.$swal) {
            this.$swal.fire({
              icon: 'error',
              title: 'Failed to delete domain',
              text: e && e.message ? e.message : 'Unknown error',
              timer: 3000,
              showConfirmButton: false
            });
          }
        }
      },
      validateDomainName(name) {
        if (!name) {
          return 'Domain can not be empty';
        }
        const trimmed = name.trim().toLowerCase();
        if (trimmed.indexOf(' ') >= 0) {
          return 'Domain can not contain white spaces';
        }
        if (trimmed.indexOf('/') >= 0 || trimmed.indexOf('http:') >= 0 || trimmed.indexOf('https:') >= 0) {
          return 'Domain can not contain protocol or slashes';
        }
        const domainRegex = /^[a-zA-Z0-9][a-zA-Z0-9-]{1,61}[a-zA-Z0-9](?:\.[a-zA-Z]{2,})+$/;
        if (!domainRegex.test(trimmed)) {
          return 'Invalid domain name';
        }
        return '';
      },
      openAddDomainDialog() {
        this.newDomainName = '';
        this.newDomainError = '';
        this.showAddDomainDialog = true;
      },
      cancelAddDomain() {
        this.showAddDomainDialog = false;
        this.newDomainName = '';
        this.newDomainError = '';
      },
      closeArenaDialog() {
        if (this.arenaImporting) return;
        this.arenaDialog = false;
      },
      closeAddGamesDialog() {
        this.dialog = false;
        this.selectedGames = [];
        this.addGameVersionById = {};
      },
      async addDomain() {
        this.newDomainError = '';
        if (!this.item || !this.item.id) {
          this.newDomainError = 'Save feed before adding domains';
          return;
        }
        const error = this.validateDomainName(this.newDomainName);
        if (error) {
          this.newDomainError = error;
          return;
        }
        const name = this.newDomainName.trim().toLowerCase();
        // prevent duplicates in current list
        if (this.domains.some(d => (d.name || d).toLowerCase() === name)) {
          this.newDomainError = 'Domain is already linked to this feed';
          return;
        }
        this.addDomainLoading = true;
        try {
          if (this.$msal && typeof this.$msal.ensureAuthenticated === 'function') {
            await this.$msal.ensureAuthenticated();
          }
        } catch (e) {
          console.error('Authentication failed before adding domain:', e);
        }
        try {
          await this.$gameApi.post('/domains', {
            name,
            feedId: this.item.id
          });
          this.cancelAddDomain();
          await this.loadDomains();
          if (this.$swal) {
            this.$swal.fire({
              icon: 'success',
              title: 'Domain added to feed.',
              timer: 1500,
              showConfirmButton: false
            });
          }
        } catch (e) {
          console.error('Error adding domain to feed:', e);
          this.newDomainError = e && e.message ? e.message : 'Failed to add domain';
        } finally {
          this.addDomainLoading = false;
        }
      },
      async openArenaDialog() {
        // Always open a clean Arena import form
        this.arenaDialog = true;
        this.arenaError = '';
        this.arenaUrlError = '';
        this.arenaSearch = '';
        this.arenaGames = [];
        this.arenaUrl = '';
        this.arenaLocale = 'en';
        this.arenaImportedGameIds = [];
        this.arenaImportedAliases = [];
        this.arenaAllMetaTemplates = null;
        this.arenaCreatedMetaTemplatesByName = {};
        this.arenaMetaTemplateSupportedGameIdsById = {};
        this.arenaMetaTemplateHasLocaleById = {};
        this.arenaImporting = false;
        this.arenaResolvedAliasesFromMetas = [];
        await this.preloadFeedAliasesFromMetaTemplates();
      },
      isValidArenaXmlUrl(url) {
        return typeof url === 'string' && url.trim() && url.includes('/arenaapi');
      },
      async fetchArenaXmlText(url) {
        const response = await fetch(url);
        if (!response.ok) {
          throw new Error(`Failed to load Arena XML (status ${response.status})`);
        }
        return await response.text();
      },
      parseXmlDocument(text) {
        const parser = new DOMParser();
        const xml = parser.parseFromString(text, 'application/xml');
        const parseError = xml.querySelector('parsererror');
        if (parseError) {
          throw new Error('Failed to parse Arena XML');
        }
        return xml;
      },
      getTextContent(node, tagName) {
        const el = node.getElementsByTagName(tagName)[0];
        return el && el.textContent ? el.textContent.trim() : '';
      },
      extractSlugFromArenaUrl(urlText) {
        if (!urlText) return '';
        try {
          const u = new URL(urlText);
          const parts = u.pathname.split('/').filter(Boolean);
          if (parts.length >= 2) {
            return parts[parts.length - 2];
          }
        } catch (e) {
          // ignore invalid URL
        }
        return '';
      },
      extractSlugFromArenaKey(key) {
        if (!key) return '';
        let slug = String(key);
        slug = slug.replace(/^(webgl-|html5-)/, '');
        slug = slug.replace(/-(phaser|pixijs)$/, '');
        return slug;
      },
      resolveCatalogGameByXmlSlug(existingGames, xmlSlug) {
        if (!xmlSlug) return null;
        const exact = existingGames.find(g => g && g.slug === xmlSlug);
        if (exact) return exact;
        const slugNoDash = String(xmlSlug).replaceAll('-', '');
        const dashInsensitiveMatches = existingGames.filter(g => {
          const gs = g?.slug ? String(g.slug) : '';
          return gs && gs.replaceAll('-', '') === slugNoDash;
        });
        return dashInsensitiveMatches.length === 1 ? dashInsensitiveMatches[0] : null;
      },
      computeAutoPickedGameId(autoPickedGameIds, matchedGame) {
        const id = matchedGame?.id;
        if (id == null) return null;
        const idNum = Number(id);
        if (autoPickedGameIds.has(idNum)) return null;
        autoPickedGameIds.add(idNum);
        return id;
      },
      buildArenaXmlRow({ name, key, url, slug, selectedGameId }) {
        return {
          name,
          key,
          url,
          slug,
          selectedGameId,
          alias: '',
          metaTemplateName: '',
          metaTemplateId: null
        };
      },
      parseArenaXmlGames(xml, existingGames) {
        const autoPickedGameIds = new Set();
        const out = [];
        for (const node of Array.from(xml.getElementsByTagName('game'))) {
          const name = this.getTextContent(node, 'name');
          const key = this.getTextContent(node, 'key');
          const urlText = this.getTextContent(node, 'url');
          if (!name || (!key && !urlText)) continue;

          const xmlSlugRaw =
            this.extractSlugFromArenaUrl(urlText) || this.extractSlugFromArenaKey(key);
          const xmlSlug = String(xmlSlugRaw || '').trim();

          const matched = this.resolveCatalogGameByXmlSlug(existingGames, xmlSlug);
          const selectedGameId = this.computeAutoPickedGameId(autoPickedGameIds, matched);

          out.push(
            this.buildArenaXmlRow({
              name,
              key,
              url: urlText,
              slug: xmlSlug,
              selectedGameId
            })
          );
        }
        return out;
      },
      async loadArenaXml() {
        if (!this.arenaUrl || typeof this.arenaUrl !== 'string') {
          this.arenaUrlError = 'URL is required';
          return;
        }
        if (!this.arenaUrl.includes('/arenaapi')) {
          this.arenaUrlError = 'URL must contain "/arenaapi".';
          return;
        }
        this.arenaUrlError = '';
        if (this.arenaLoading) {
          return;
        }
        this.arenaLoading = true;
        this.arenaError = '';
        this.arenaGames = [];
        try {
          if (!this.isValidArenaXmlUrl(this.arenaUrl)) {
            this.arenaUrlError = 'URL must contain "/arenaapi".';
            return;
          }
          const text = await this.fetchArenaXmlText(this.arenaUrl);
          const xml = this.parseXmlDocument(text);
          const existingGames = Array.isArray(this.games) ? this.games : [];
          this.arenaGames = this.parseArenaXmlGames(xml, existingGames);
          for (const row of this.arenaGames) {
            if (row?.selectedGameId) {
              this.onArenaSelectedGameChange(row);
            }
          }
        } catch (error) {
          console.error('Error loading Arena XML:', error);
          this.arenaError = error && error.message ? error.message : 'Unknown error while loading Arena XML';
        } finally {
          this.arenaLoading = false;
        }
      },
      async importArenaGames() {
        if (!this.item || !this.item.id) {
          return;
        }
        if (this.arenaImporting) {
          return;
        }
        this.arenaImporting = true;
        try {
          // Try to ensure authentication before making API calls (if MSAL plugin is available)
          if (this.$msal && typeof this.$msal.ensureAuthenticated === 'function') {
            await this.$msal.ensureAuthenticated();
          }
        } catch (e) {
          console.error('Authentication failed before importing Arena games:', e);
        }
        try {
          const existingGames = Array.isArray(this.games) ? this.games : [];
          const feedIds = this.effectiveFeedGameIdSet;
          const mappedGames = [];
          for (const arenaGame of this.arenaGames) {
            const gameId = arenaGame.selectedGameId;
            if (!gameId) {
              continue;
            }
            // Safety: even if user selected an already-added game, skip it.
            if (feedIds.has(Number(gameId))) {
              continue;
            }
            const game = existingGames.find(g => g.id === gameId);
            if (!game) {
              continue;
            }
            mappedGames.push({
              game,
              alias: arenaGame.alias || '',
              metaTemplateName: arenaGame.metaTemplateName || '',
              metaTemplateId: arenaGame.metaTemplateId || null,
              needsAlias: this.arenaNeedsAlias(arenaGame)
            });
          }

          if (!mappedGames.length) {
            if (this.$swal) {
              this.$swal.fire({
                icon: 'warning',
                title: 'No games selected',
                text: 'Please select at least one game that is not already added to this feed.',
                timer: 3000,
                showConfirmButton: false
              });
            }
            return;
          }

          for (const entry of mappedGames) {
            const { game } = entry;
            let metaTemplateId = entry.metaTemplateId;

            if (entry.needsAlias) {
              const resolved = await this.resolveSyndicationMetaTemplate(game?.slug, entry.alias);
              if (resolved?.metaTemplateId) {
                metaTemplateId = resolved.metaTemplateId;
                if (resolved.localeToEnsure) {
                  await this.ensureMetaTemplateHasLocale(metaTemplateId, entry.alias, resolved.localeToEnsure);
                }
              }

              // Otherwise, fall back to the name coming from UI or generate a new unique one and create it.
              if (!metaTemplateId) {
                const computedName =
                  entry.metaTemplateName ||
                  this.computeSyndicationBaseMetaTemplateName(game?.slug) ||
                  this.computeSyndicationMetaTemplateName(game?.slug);
                const name = String(computedName || '').trim();
                if (name) {
                  const existing = await this.findMetaTemplateByExactName(name);
                  if (existing?.id) {
                    metaTemplateId = existing.id;
                  } else if (this.arenaCreatedMetaTemplatesByName && this.arenaCreatedMetaTemplatesByName[name]) {
                    metaTemplateId = this.arenaCreatedMetaTemplatesByName[name];
                  } else {
                    const created = await this.$gameApi.post('/meta-templates', {
                      name,
                      description: '',
                      supportedGames: [{ id: game.id }]
                    });
                    if (created?.id) {
                      metaTemplateId = created.id;
                      this.arenaCreatedMetaTemplatesByName[name] = created.id;
                    }
                  }
                }
              }
            }

            // If we have a metaTemplateId for this game, ensure it supports this game
            // and has the selected locale meta with alias (so downstream systems resolve alias properly).
            if (metaTemplateId && entry.needsAlias) {
              await this.ensureMetaTemplateSupportsGame(metaTemplateId, game.id);
              await this.ensureMetaTemplateHasLocale(metaTemplateId, entry.alias, this.getArenaLocale());
            }

            const payload = { gameId: game.id, feedId: this.item.id };
            if (entry.alias && String(entry.alias).trim()) {
              payload.alias = String(entry.alias).trim();
            }
            if (metaTemplateId) {
              payload.metaTemplateId = metaTemplateId;
            }
            await this.$gameApi.post(`/feeds/${this.item.id}/games/`, payload);
            // Mark as imported locally so the table hides it immediately.
            // (Parent refresh may lag; this keeps the dialog in sync.)
            if (!this.arenaImportedGameIds.includes(game.id)) {
              this.arenaImportedGameIds.push(game.id);
            }
            if (entry.needsAlias && entry.alias && !this.arenaImportedAliases.includes(entry.alias)) {
              this.arenaImportedAliases.push(entry.alias);
            }
            await this.sleep(100);
          }
          this.$emit('change', this.item);
          await this.loadDomains();
          if (this.$swal) {
            this.$swal.fire({
              icon: 'success',
              title: 'Games imported from Arena XML.',
              timer: 2000,
              showConfirmButton: false
            });
          }
        } catch (error) {
          console.error('Error importing Arena games:', error);
          if (this.$swal) {
            this.$swal.fire({
              icon: 'error',
              title: 'Failed to import games from Arena XML',
              text: error && error.message ? error.message : 'Unknown error',
              timer: 3000,
              showConfirmButton: false
            });
          }
        } finally {
          this.arenaImporting = false;
        }
      },
    exportDomainsToExcel() {
      if (this.domains.length === 0) return;
      
      // Create CSV content with all domains (not just current page)
      const headers = ['Domain Name', 'Feed Name', 'Environment'];
      const csvContent = [
        headers.join(','),
        ...this.domains.map(domain => [
          domain.name || domain,
          this.item.name,
          this.item.environment
        ].join(','))
      ].join('\n');
      
      // Create and download file
      const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
      const link = document.createElement('a');
      const url = URL.createObjectURL(blob);
      link.setAttribute('href', url);
      link.setAttribute('download', `feed_${this.item.name}_domains.csv`);
      link.style.visibility = 'hidden';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      
      // Show success message
      this.$swal.fire({
        icon: 'success',
        title: 'Export successful!',
        text: `Exported ${this.domains.length} domains to CSV file.`,
        timer: 2000,
        showConfirmButton: false
      });
    },
    editArena(domain) {
      const domainName = domain.name || domain;
      console.log('=== EDIT ARENA DEBUG ===');
      console.log('Domain object:', domain);
      console.log('Domain name:', domainName);
      
      // Store domain info in sessionStorage for the arena page to use
      sessionStorage.setItem('searchArenaDomain', domainName);
      console.log('Stored in sessionStorage:', domainName);
      
      // Navigate immediately
      console.log('Navigating to /arena-global-admin');
      this.$router.push('/arena-global-admin');
    }
  }
};
</script>

<style scoped>
.feed-form {
  width: 100%;
}

.feed-form__tabs-shell {
  border: 1px solid rgba(0, 0, 0, 0.22);
  border-radius: 8px;
  overflow: hidden;
  background: #fff;
  margin-bottom: 16px;
}

.feed-form__tabs {
  border: none !important;
  border-bottom: 1px solid rgba(0, 0, 0, 0.12) !important;
  border-radius: 0 !important;
  background: #f5f5f5 !important;
  margin: 0 !important;
}

.feed-form__tabs-items {
  margin: 0;
  padding: 0;
}

.feed-form__tabs-items ::v-deep .v-window__container,
.feed-form__tabs-items ::v-deep .v-window-item {
  margin: 0 !important;
  padding: 0 !important;
}

.feed-form__tab-panel {
  margin: 0;
  border: none;
  border-radius: 0;
  background: #fff;
  min-height: 200px;
  box-shadow: none;
}

.feed-form__tab-panel-body {
  padding: 0;
  overflow-x: auto;
}

.feed-form__tab-panel-body--padded {
  padding: 16px;
}

.feed-form__tab-panel-body ::v-deep > .v-card {
  margin: 0 !important;
  border-radius: 0 !important;
  box-shadow: none !important;
  border: none !important;
}

.feed-form__section {
  border-left: 4px solid #1976d2;
  overflow: hidden;
}

.feed-form__section-title {
  display: flex;
  align-items: center;
  padding-top: 16px !important;
  padding-bottom: 12px !important;
  font-size: 1.15rem !important;
  font-weight: 600 !important;
  letter-spacing: 0.01em;
}

.feed-form__section ::v-deep > .v-card-text {
  padding-top: 4px !important;
  padding-bottom: 12px !important;
}

.feed-domains__preview {
  max-width: 100%;
  vertical-align: middle;
}

.feed-domains__actions {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  white-space: nowrap;
}

.feed-domains__table ::v-deep .v-data-table__wrapper {
  overflow-x: auto;
}

.feed-domains__table ::v-deep .feed-domains__col--compact {
  white-space: nowrap;
  width: 1%;
  padding-left: 8px;
  padding-right: 8px;
}

.domain-link {
  color: #1976d2;
  text-decoration: underline;
  text-underline-offset: 2px;
  cursor: pointer;
}

.domain-link:hover {
  color: #1565c0;
  text-decoration: underline;
}

.feed-form__games {
  overflow: hidden;
}

.feed-form__games-actions {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  white-space: nowrap;
}

.feed-form__add-game-version {
  max-width: 160px;
  min-width: 120px;
}

.feed-form__games-table ::v-deep .v-data-table__wrapper {
  overflow-x: auto;
}

.feed-form__games-table ::v-deep .feed-form__games-col--compact {
  white-space: nowrap;
  width: 1%;
  padding-left: 8px;
  padding-right: 8px;
}

.feed-game-meta-cell {
  min-width: 220px;
}

.feed-game-meta-link {
  flex-shrink: 0;
  display: inline-flex;
  align-items: center;
  color: #1976d2;
  text-decoration: none;
  line-height: 1;
}

.feed-game-meta-link:hover {
  color: #1565c0;
}

.feed-form__save {
  display: flex;
  justify-content: flex-start;
  position: sticky;
  bottom: 12px;
  z-index: 2;
  padding: 12px 16px;
  background: rgba(255, 255, 255, 0.92);
  border: 1px solid rgba(25, 118, 210, 0.2);
  border-radius: 8px;
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.08);
  backdrop-filter: blur(6px);
}
</style>
